import gql from 'graphql-tag';

// FRAGMENTS
export const FRAGMENT_MEDIA_FIELDS = gql`
  fragment MediaFields on Media {
    id
    size {
      thumbnailSmall
      preview
      original
    }
  }
`;

export const FRAGMENT_AVATAR_FIELDS = gql`
  fragment AvatarFields on Media {
    id
    filename
    type
    size {
      avatarLarge
      avatarMedium
      avatarSmall
    }
  }
`;

export const FRAGMENT_USER_FIELDS = gql`
  fragment UserFields on User {
    id
    chatStatus
    createdAt
    email
    enabled
    language
    masterKeyStatus
    MFAStatus
    preferredMfaSetting
    removalStatus
    signupReferralCode
    signupStatus
    status
    uiMode
    updatedAt
    userMFASettingList
    username
  }
`;

export const FRAGMENT_PROFILE_FIELDS = gql`
  fragment ProfileFields on Profile {
    id
    avatarId
    compareWorkspaceId
    createdAt
    creationStatus
    current
    default
    defaultWorkspaceId
    inspirationWorkspaceId
    name
    owner
    showTutorial {
      WORKSPACE
      STRATEGIES
      MERGE
      COMPARE
      ALIGN
    }
    slug
    status
    strategyWorkspaceId
    updatedAt
  }
`;

export const FRAGMENT_WORKSPACE_FIELDS = gql`
  fragment WorkspaceFields on Workspace {
    id
    description
    default
    name
    position
    system
    saved
  }
`;

export const FRAGMENT_WORKSPACE_WIDGET_FIELDS = gql`
  fragment WorkspaceWidgetFields on WorkspaceWidget {
    id
    position
    size
    locked
  }
`;

export const FRAGMENT_WIDGET_FIELDS = gql`
  fragment WidgetFields on Widget {
    id
    active
    category
    componentName
    description
    installable
    locked
    name
    position
    slug
    suggestedSize
  }
`;

export const FRAGMENT_MEMBERSHIP_FIELDS = gql`
  fragment MembershipFields on Membership {
    id
    membershipPlanId
    paid
    price
    renewal
    sku
    source
    createdAt
    updatedAt
  }
`;

export const FRAGMENT_MEMBERSHIP_PLAN_FIELDS = gql`
  fragment MembershipPlanFields on MembershipPlan {
    id
    default
    position
    title
    sku
    features {
      customizableDashboard
      communityChat
      profiles
      strategies
      strategyAssets
      widgets
      portfolioImport
      smartAlert
      swagBag
    }
    prices {
      price
      renewal
      sku
    }
  }
`;

export const FRAGMENT_MEMBERSHIP_PLAN_WIDGET_FIELDS = gql`
  fragment MembershipPlanWidgetFields on MembershipPlanWidget {
    id
    position
  }
`;

export const FRAGMENT_STRATEGY_FIELDS = gql`
  fragment StrategyFields on Strategy {
    id
    active
    archived
    color
    createdAt
    currency
    endDate
    flow
    name
    notifyEnd
    owner
    profileId
    risk
    riskAssetConcentration
    riskAssetConcentrationWeight
    riskCurrencyConcentration
    riskCurrencyConcentrationWeight
    riskGeographicalConcentration
    riskGeographicalConcentrationWeight
    riskIndustryConcentration
    riskIndustryConcentrationWeight
    slug
    startDate
    status
    tags
    updatedAt
    workspaceId
  }
`;

export const FRAGMENT_STRATEGY_ASSET_FIELDS = gql`
  fragment StrategyAssetFields on StrategyAsset {
    alert
    archivedAt
    assetId
    bank
    connectionType
    currentPrice
    currentPriceInReportingCurrency
    currentPriceInAssetCurrency
    entryPrice
    entryPriceInReportingCurrency
    entryPriceInAssetCurrency
    entryExchangePrice
    id
    inspirationSource
    name
    purchasedAt
    strategyAssetCategoryId
    strategyId
    type
    unit
  }
`;

export const FRAGMENT_STRATEGY_ASSET_TARGET_FIELDS = gql`
  fragment StrategyAssetTargetFields on StrategyAssetTarget {
    id
    active
    alert
    endDate
    stopLoss
    stopLossPercentage
    stopLossInReportingCurrency
    stopLossInAssetCurrency
    stopProfit
    stopProfitPercentage
    stopProfitInAssetCurrency
    stopProfitInReportingCurrency
  }
`;

export const FRAGMENT_STRATEGY_ASSET_CATEGORY_FIELDS = gql`
  fragment StrategyAssetCategoryFields on StrategyAssetCategory {
    id
    key
    title
    type
  }
`;

export const FRAGMENT_ASSET_FIELDS = gql`
  fragment AssetFields on Asset {
    id
    logo
    currency
    country
    region
    industry
    name
    providerId
    symbol
    type
    historicalIndex
    exchange
    marketCap
    volume
  }
`;

export const FRAGMENT_ASSET_ESSENTIAL_FIELDS = gql`
  fragment AssetEssentialFields on Asset {
    id
    exchange
    logo
    name
    symbol
    type
    currency
  }
`;

export const FRAGMENT_ASSET_SERIES_FIELDS = gql`
  fragment AssetSeriesFields on AssetSeries {
    date
    eur
    usd
    cur
  }
`;

export const FRAGMENT_ASSET_INFO_FIELDS = gql`
  fragment AssetInfoFields on AssetInfo {
    id
    data
  }
`;

export const FRAGMENT_KNOWLEDGE_POST_FIELDS = gql`
  fragment KnowledgePostFields on KnowledgePost {
    id
    content
    createdAt
    position
    slug
    title
  }
`;

export const FRAGMENT_KNOWLEDGE_CATEGORY_FIELDS = gql`
  fragment KnowledgeCategoryFields on KnowledgeCategory {
    id
    name
  }
`;

export const FRAGMENT_CHATROOM_WITH_SETTINGS_FIELDS = gql`
  fragment ChatRoomWithSettingsFields on ChatRoomWithSettings {
    id
    title
    slug
    disabled
    notificationsEnabled
    soundEnabled
  }
`;

export const FRAGMENT_CHAT_MESSAGE_FIELDS = gql`
  fragment ChatMessageFields on ChatMessage {
    id
    chatRoomId
    type
    ownerId
    ownerProfile {
      id
      name
    }
    content
    resource
    type
    sentAt
  }
`;

export const FRAGMENT_CHAT_MESSAGE_RESPONSE_FIELDS = gql`
  fragment ChatMessageResponseFields on ChatMessageResponse {
    action
    id
    chatRoomId
    type
    ownerId
    ownerProfile {
      id
      name
    }
    content
    resource
    type
    sentAt
  }
`;

export const FRAGMENT_STRATEGY_RISK_FIELDS = gql`
  fragment StrategyRiskFields on Strategy {
    risk
    riskAssetConcentration
    riskGeographicalConcentration
    riskCurrencyConcentration
    riskIndustryConcentration
  }
`;

// Risk rate
export const FRAGMENT_STRATEGY_RISK_CONCENTRATION_WEIGHTS = gql`
  fragment StrategyRiskWeights on Strategy {
    riskCurrencyConcentrationWeight
    riskIndustryConcentrationWeight
    riskAssetConcentrationWeight
    riskGeographicalConcentrationWeight
  }
`;

// QR Auth
export const FRAGMENT_QR_AUTH_FIELDS = gql`
  fragment QRAuthFields on QRAuth {
    createdAt
    device
    deviceName
    email
    id
    owner
    qr
    status
    updatedAt
  }
`;

// Notifications
export const FRAGMENT_ASSET_MAIN_FIELDS = gql`
  fragment AssetMainFields on Asset {
    id
    exchange
    logo
    symbol
    name
    type
  }
`;

export const FRAGMENT_ALERT_NOTIFICATION_SETTINGS = gql`
  fragment AlertNotificationSettingsField on AlertNotificationSetting {
    owner
    priceAlert
    stopProfitAlert
    stopLossAlert
    strategyEnd
    newFeature
  }
`;

export const FRAGMENT_ALERT_NOTIFICATION = gql`
  fragment AssetAlertNotificationFields on AlertNotification {
    id
    notificationType
    owner
    assetAlertId
    assetId
    strategyAssetId
    alertCurrency
    alertValue
    alertName
    assetName
    assetValue
    assetCurrency
    assetLogo
    assetType
    exchange
    strategyName
    strategyId
    strategyName
    strategyColor
    stopProfit
    stopLoss
    createdAt
  }
`;

export const FRAGMENT_ASSET_ALERT = gql`
  fragment AssetAlert on AssetAlert {
    id
    assetId
    name
    value
    currency
    expireAt
    asset {
      name
      logo
      symbol
      type
      currency
    }
  }
`;

export const FRAGMENT_VARIATIONS = gql`
  fragment Variations on VariationsByInterval {
    daily {
      best {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
      worst {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
    }
    weekly {
      best {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
      worst {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
    }
    monthly {
      best {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
      worst {
        assetId
        variation
        startDate
        endDate
        startValue
        endValue
      }
    }
  }
`;
